// src/pages/DebtStudio/components/Portfolios/DebtService/index.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import SectionCard from "components/SectionCard";
import ExcelGrid from "components/ExcelGrid";
import TabMenu from "components/TabMenu";
import { DateInput } from "components/InputStyled";
import { supabase } from "services/supabaseClient";
import { useProfile } from "context/ProfileContext";
import dayjs from "lib/dayjs";

import {
  buildPortfolioDebtService,
  buildPortfolioDebtServicePeriodRows,
} from "pages/DebtStudio/lib/buildPortfolioDebtService";

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import ChartTooltip from "components/ChartTooltip";
import {
  ALL_DIMENSION_VALUES,
  DimensionValueCompositeSelect,
  includeDimensionValueIds,
  selectedDimensionValueIds,
} from "components/DimensionValueFilter";
import { makeNiceScaleZeroBased } from "lib/chartScale";
import exportDebtPortfolioPeriodPdf from "pages/DebtStudio/reports/exportDebtPortfolioPeriodPdf";

/* ------------------------------------------------------------------
 * Chart helpers
 * ------------------------------------------------------------------ */

/**
 * Use distinct color pools so:
 *  - Chart 1 (P&I) uses chart-1 / chart-2
 *  - Chart 2 (by issue) does NOT reuse chart-1 / chart-2, avoiding visual confusion
 */
const CHART_VARS_PI = {
  principal: "var(--chart-1)",
  interest: "var(--chart-2)",
};

// Start at chart-3 so issue colors are different from the P&I chart.
const CHART_VARS_ISSUES = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "var(--chart-6)",
  "var(--chart-7)",
  "var(--chart-8)",
  "var(--chart-9)",
  "var(--chart-10)",
  "var(--chart-11)",
  "var(--chart-12)",
];

function formatKMB(v) {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  if (a >= 1e9)
    return `${(n / 1e9).toLocaleString(undefined, { maximumFractionDigits: 1 })}B`;
  if (a >= 1e6)
    return `${(n / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 })}M`;
  if (a >= 1e3)
    return `${(n / 1e3).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
  return n.toLocaleString();
}

function formatFull2dp(v, s = "$") {
  return `${s}${(Number(v) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtMoney2(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtPeriodEndMmDd(dateStr) {
  if (!dateStr) return "(not set)";
  const d = dayjs(dateStr);
  if (!d.isValid()) return dateStr;
  return d.format("MM-DD");
}

function periodLabel(dateStr) {
  const d = dayjs(dateStr);
  if (!d.isValid()) return dateStr;
  // Period end labels for x-axis
  return d.format("YYYY-MM-DD");
}

/* ------------------------------------------------------------------
 * KPI helpers
 * ------------------------------------------------------------------ */

function n(x) {
  const v = Number(x);
  return Number.isFinite(v) ? v : 0;
}
function round2(x) {
  return Math.round((n(x) + Number.EPSILON) * 100) / 100;
}

function computeDebtServiceKpis(aggregateFY) {
  const fy = aggregateFY || [];

  const totalPrincipal = round2(fy.reduce((s, r) => s + n(r?.principal), 0));
  const totalInterest = round2(fy.reduce((s, r) => s + n(r?.interest), 0));
  const totalDebtService = round2(fy.reduce((s, r) => s + n(r?.total), 0));

  const avgAnnualDebtService =
    fy.length > 0 ? round2(totalDebtService / fy.length) : null;

  const current = fy[0] || null;
  const next = fy[1] || null;

  return {
    totalPrincipal,
    totalInterest,
    totalDebtService,
    avgAnnualDebtService,
    currentFY: current, // { date, principal, interest, total }
    nextFY: next, // { date, principal, interest, total }
  };
}

function KpiTile({ label, value, description }) {
  return (
    <div
      className="rounded-lg border border-border bg-app p-3"
      title={description || ""}
    >
      <div className="text-xs text-muted">{label}</div>
      <div className="mt-1 text-lg font-semibold text-app leading-tight">
        {value}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------ */

const MEMBER_SELECT = `
  id,
  ds_deal_id,
  include,
  sort_order,
  created_at,
  ds_deals:ds_deal_id (
    *,
    issuers:issuer_id (
      legal_name
    )
  )
`;

const DEBT_SERVICE_TABS = [
  { key: "aggregate", label: "Aggregate" },
  { key: "period", label: "Period" },
];

function todayIso() {
  return dayjs().format("YYYY-MM-DD");
}

function monthStartIso(dateIso) {
  const date = dayjs(dateIso);
  return (date.isValid() ? date : dayjs()).startOf("month").format("YYYY-MM-DD");
}

function PdfButton({ onClick, generating, disabled = false, title }) {
  if (typeof onClick !== "function") return null;

  return (
    <button
      type="button"
      className="btn btn-primary shrink-0 text-sm"
      onClick={onClick}
      disabled={disabled || generating}
      title={title}
    >
      {generating ? "Generating PDF..." : "Export PDF"}
    </button>
  );
}

export default function PortfoliosDebtService({
  portfolio,
  periodEndDate,
  onExportPdf = null,
  isPdfGenerating = false,
  issueFilter = ALL_DIMENSION_VALUES,
  onIssueFilterChange = null,
}) {
  const { selectedOrg } = useProfile();

  const portfolioId = portfolio?.id || null;
  const orgId = selectedOrg?.id || null;

  // Draft period end should override persisted, so DS reacts immediately on Main edits.
  const effectivePeriodEnd = useMemo(() => {
    const draft = typeof periodEndDate === "string" ? periodEndDate : "";
    const persisted =
      typeof portfolio?.period_end === "string" ? portfolio.period_end : "";
    return draft || persisted || "";
  }, [periodEndDate, portfolio?.period_end]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [members, setMembers] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState("aggregate");
  const [reportType, setReportType] = useState("date");
  const [selectedDate, setSelectedDate] = useState(todayIso);
  const [startDate, setStartDate] = useState(() => monthStartIso(todayIso()));
  const [endDate, setEndDate] = useState(todayIso);
  const [generatedCriteria, setGeneratedCriteria] = useState(null);
  const [periodPdfGenerating, setPeriodPdfGenerating] = useState(false);
  const [periodPdfError, setPeriodPdfError] = useState("");

  const fetchMembers = useCallback(async () => {
    if (!portfolioId || !orgId) return;

    setLoading(true);
    setError("");

    try {
      const { data, error: err } = await supabase
        .from("ds_portfolio_issues")
        .select(MEMBER_SELECT)
        .eq("org_id", orgId)
        .eq("portfolio_id", portfolioId)
        .order("sort_order", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: true });

      if (err) throw err;

      setMembers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("❌ [PortfoliosDebtService] fetch members failed:", e);
      setError(e?.message || "Failed to load portfolio issues.");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, orgId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    setGeneratedCriteria(null);
  }, [portfolioId]);

  const fullDebtService = useMemo(() => {
    // IMPORTANT: Use the portfolio period end (draft/persisted) as the annual bucket end.
    return buildPortfolioDebtService({
      members,
      periodEndOverride: effectivePeriodEnd || null,
    });
  }, [members, effectivePeriodEnd]);
  const issues = fullDebtService.issues;

  const issueSeries = useMemo(() => {
    return (issues || []).map((it, idx) => ({
      issueId: it.issueId,
      key: `issue_${it.issueId}`,
      name: it.label,
      color: CHART_VARS_ISSUES[idx % CHART_VARS_ISSUES.length],
    }));
  }, [issues]);

  const issueFilterItems = useMemo(
    () => issueSeries.map((series) => ({ id: series.issueId, label: series.name })),
    [issueSeries]
  );
  const selectedIssueIds = useMemo(
    () => selectedDimensionValueIds(issueFilter, issueFilterItems),
    [issueFilter, issueFilterItems]
  );
  const filteredMembers = useMemo(
    () => (members || []).filter((member) => {
      const deal = member?.ds_deals || member?.ds_deals_row || member?.deal;
      return deal?.id && selectedIssueIds.has(deal.id);
    }),
    [members, selectedIssueIds]
  );
  const { aggregateFY, stackedByIssueFY } = useMemo(
    () => buildPortfolioDebtService({
      members: filteredMembers,
      periodEndOverride: effectivePeriodEnd || null,
    }),
    [effectivePeriodEnd, filteredMembers]
  );

  const kpis = useMemo(() => {
    return computeDebtServiceKpis(aggregateFY || []);
  }, [aggregateFY]);

  const currentLabel = useMemo(() => {
    const d = kpis?.currentFY?.date;
    return d ? `to ${d}` : "";
  }, [kpis?.currentFY?.date]);

  const nextLabel = useMemo(() => {
    const d = kpis?.nextFY?.date;
    return d ? `to ${d}` : "";
  }, [kpis?.nextFY?.date]);
  const visibleIssueSeries = useMemo(
    () => issueSeries.filter((series) => selectedIssueIds.has(series.issueId)),
    [issueSeries, selectedIssueIds]
  );
  const maxAgg = useMemo(() => {
    let m = 0;
    for (const r of aggregateFY || []) m = Math.max(m, Number(r.total) || 0);
    return m;
  }, [aggregateFY]);

  const maxStack = useMemo(() => {
    let m = 0;
    for (const r of stackedByIssueFY || []) m = Math.max(m, Number(r.total) || 0);
    return m;
  }, [stackedByIssueFY]);

  const aggScale = useMemo(() => makeNiceScaleZeroBased(maxAgg, 6, 1), [maxAgg]);
  const stackScale = useMemo(
    () => makeNiceScaleZeroBased(maxStack, 6, 1),
    [maxStack]
  );

  const yTickFormatter = useCallback((v) => `$${formatKMB(v)}`, []);

  // Hide any tooltip row where the value is 0 (or effectively 0).
  // Recharts passes (value, name, entry, index). We return null to omit the row.
  const tooltipFormatter = useCallback((val, name) => {
    const n = Number(val) || 0;
    if (Math.abs(n) < 1e-9) return null;
    return [formatFull2dp(n, "$"), name];
  }, []);

  // TABLE: remove the extra "Period" column; show only the period-end date + totals.
  const tableRows = useMemo(() => {
    return (aggregateFY || []).map((r) => ({
      period_end: r.date,
      principal: r.principal,
      interest: r.interest,
      total: r.total,
    }));
  }, [aggregateFY]);

  const byIssueTableRows = useMemo(() => {
    return (issues || []).filter((issue) => selectedIssueIds.has(issue.issueId)).map((issue) => {
      const totals = (issue.annualTotals || []).reduce(
        (acc, row) => {
          acc.principal += n(row?.principal);
          acc.interest += n(row?.interest);
          acc.total += n(row?.total);
          return acc;
        },
        { principal: 0, interest: 0, total: 0 }
      );

      return {
        issue: issue.label || "Issue",
        principal: totals.principal,
        interest: totals.interest,
        total: totals.total,
      };
    });
  }, [issues, selectedIssueIds]);

  const columns = useMemo(() => {
    const money = (v) =>
      v != null
        ? Number(v).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "";

    const rightAlign = "text-right numeric";

    return [
      {
        key: "period_end",
        label: "Period End",
        date: true,
        width: "w-40",
        className: "numeric",
      },
      {
        key: "principal",
        label: "Principal",
        numeric: true,
        width: "w-36",
        className: rightAlign,
        headerClassName: "text-right",
        format: money,
      },
      {
        key: "interest",
        label: "Interest",
        numeric: true,
        width: "w-36",
        className: rightAlign,
        headerClassName: "text-right",
        format: money,
      },
      {
        key: "total",
        label: "Total",
        numeric: true,
        width: "w-36",
        className: rightAlign,
        headerClassName: "text-right",
        format: money,
      },
    ];
  }, []);

  const byIssueColumns = useMemo(() => {
    const money = (v) =>
      v != null
        ? Number(v).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "";

    const rightAlign = "text-right numeric";

    return [
      {
        key: "issue",
        label: "Issue",
        width: "min-w-72",
        className: "text-left",
      },
      {
        key: "principal",
        label: "Principal",
        numeric: true,
        width: "w-36",
        className: rightAlign,
        headerClassName: "text-right",
        format: money,
      },
      {
        key: "interest",
        label: "Interest",
        numeric: true,
        width: "w-36",
        className: rightAlign,
        headerClassName: "text-right",
        format: money,
      },
      {
        key: "total",
        label: "Total",
        numeric: true,
        width: "w-36",
        className: rightAlign,
        headerClassName: "text-right",
        format: money,
      },
    ];
  }, []);

  const hasData = (fullDebtService.aggregateFY || []).length > 0;

  const currentCriteria = useMemo(
    () => ({ reportType, selectedDate, startDate, endDate }),
    [endDate, reportType, selectedDate, startDate]
  );
  const currentCriteriaKey = JSON.stringify(currentCriteria);
  const generatedCriteriaKey = generatedCriteria
    ? JSON.stringify(generatedCriteria)
    : "";
  const reportRefreshRequired =
    !!generatedCriteria && generatedCriteriaKey !== currentCriteriaKey;

  const periodRows = useMemo(() => {
    if (!generatedCriteria) return [];
    return buildPortfolioDebtServicePeriodRows({
      issues,
      ...generatedCriteria,
    });
  }, [generatedCriteria, issues]);

  const periodTotals = useMemo(
    () =>
      periodRows.reduce(
        (totals, row) => ({
          principal: round2(totals.principal + n(row.principal)),
          interest: round2(totals.interest + n(row.interest)),
          total: round2(totals.total + n(row.total)),
        }),
        { principal: 0, interest: 0, total: 0 }
      ),
    [periodRows]
  );

  const periodColumns = useMemo(() => {
    const money = (value) =>
      Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    const numeric = "text-right numeric";
    return [
      { key: "date", label: "Date", date: true, width: "w-40" },
      { key: "issue", label: "Issue", width: "min-w-72" },
      { key: "principal", label: "Principal", numeric: true, width: "w-36", className: numeric, headerClassName: "text-right", format: money },
      { key: "interest", label: "Interest", numeric: true, width: "w-36", className: numeric, headerClassName: "text-right", format: money },
      { key: "total", label: "Debt Service", numeric: true, width: "w-36", className: numeric, headerClassName: "text-right", format: money },
    ];
  }, []);

  const periodCriteriaValid =
    reportType === "period"
      ? !!startDate && !!endDate && startDate <= endDate
      : !!selectedDate;

  const handlePeriodPdfExport = useCallback(async () => {
    if (!generatedCriteria || reportRefreshRequired || !periodRows.length) return;
    setPeriodPdfGenerating(true);
    setPeriodPdfError("");
    try {
      await exportDebtPortfolioPeriodPdf({
        orgId: selectedOrg?.id,
        portfolioName: portfolio?.name || "Portfolio",
        orgName: selectedOrg?.name || "",
        criteria: generatedCriteria,
        rows: periodRows,
        totals: periodTotals,
      });
    } catch (exportError) {
      console.error("DebtStudio period PDF export failed:", exportError);
      setPeriodPdfError(exportError?.message || "Unable to export the Period report.");
    } finally {
      setPeriodPdfGenerating(false);
    }
  }, [
    generatedCriteria,
    reportRefreshRequired,
    periodRows,
    periodTotals,
    portfolio?.name,
    selectedOrg?.id,
    selectedOrg?.name,
  ]);

  return (
    <div className="space-y-4">
      <TabMenu
        tabs={DEBT_SERVICE_TABS}
        activeTab={activeSubTab}
        onChange={setActiveSubTab}
        ariaLabel="Debt Service reports"
      />

      {activeSubTab === "aggregate" ? (
      <SectionCard
        title="Aggregate Debt Service"
        headerAction={
          <div className="flex flex-wrap items-end justify-end gap-2">
            <DimensionValueCompositeSelect
              label="Issues"
              items={issueFilterItems}
              selection={issueFilter}
              onChange={onIssueFilterChange}
            />
            <PdfButton
              onClick={onExportPdf}
              generating={isPdfGenerating}
              title="Export full DebtStudio - Portfolio Report to PDF"
            />
          </div>
        }
      >
        <div className="mt-2 text-sm">
          <span className="text-muted">Annual Period End: </span>
          <span className="text-app">{fmtPeriodEndMmDd(effectivePeriodEnd)}</span>
        </div>

        {error ? (
          <div className="mt-3 rounded-md border border-border bg-app p-3 text-sm">
            <div className="text-red-600 font-medium">Issue</div>
            <div className="text-muted mt-1">{error}</div>
          </div>
        ) : null}

        {!hasData && !loading && !error ? (
          <div className="mt-4 p-4 text-muted">
            No debt service data available. Add issues to this portfolio (Issues tab)
            and ensure each issue has a saved structure.
          </div>
        ) : null}

        {hasData && (
          <div className="mt-4 space-y-4">
            {/* KPI strip */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <KpiTile
                label="Total Principal (forward)"
                value={fmtMoney2(kpis.totalPrincipal)}
                description="Sum of scheduled principal payments from the current period forward."
              />
              <KpiTile
                label="Total Interest (forward)"
                value={fmtMoney2(kpis.totalInterest)}
                description="Sum of scheduled interest payments from the current period forward."
              />
              <KpiTile
                label="Total Debt Service (forward)"
                value={fmtMoney2(kpis.totalDebtService)}
                description="Sum of scheduled principal + interest from the current period forward."
              />
              <KpiTile
                label="Average Annual Debt Service (forward)"
                value={
                  kpis.avgAnnualDebtService == null
                    ? "—"
                    : fmtMoney2(kpis.avgAnnualDebtService)
                }
                description="Average annual principal + interest across the displayed forward periods."
              />
              <KpiTile
                label={`Current Period Debt Service${
                  currentLabel ? ` (${currentLabel})` : ""
                }`}
                value={
                  kpis.currentFY?.total != null ? fmtMoney2(kpis.currentFY.total) : "—"
                }
                description="Total debt service in the current annual period bucket."
              />
              <KpiTile
                label={`Next Period Debt Service${nextLabel ? ` (${nextLabel})` : ""}`}
                value={kpis.nextFY?.total != null ? fmtMoney2(kpis.nextFY.total) : "—"}
                description="Total debt service in the next annual period bucket."
              />
            </div>

            {/* Charts + table */}
            <div className="grid grid-cols-1 gap-4">
              {/* Chart 1 (TOP): Total DS stacked by issue */}
              <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
                <h3 className="mb-2 text-lg font-semibold text-brand">By Issue</h3>

                <div className="bg-card border border-border rounded-xl p-4 shadow-sm h-96">
                  {visibleIssueSeries.length ? <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={stackedByIssueFY.map((r) => ({
                        ...r,
                        period: periodLabel(r.date),
                      }))}
                      margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis
                        domain={[stackScale.niceMin, stackScale.niceMax]}
                        ticks={stackScale.ticks}
                        tickFormatter={yTickFormatter}
                      />
                      <Tooltip
                        content={(props) => (
                          <ChartTooltip
                            {...props}
                            formatter={tooltipFormatter}
                            className="text-xs"
                          />
                        )}
                      />
                      {visibleIssueSeries.map((s) => (
                        <Bar
                          key={s.key}
                          dataKey={s.key}
                          stackId="a"
                          fill={s.color}
                          name={s.name}
                          isAnimationActive={false}
                        />
                      ))}
                    </ComposedChart>
                  </ResponsiveContainer> : (
                    <div className="flex h-full items-center justify-center text-sm text-muted">
                      No issues selected. Use the Issues filter to choose chart constituents.
                    </div>
                  )}
                </div>

                {/* Keep hidden issues in the legend so they can be restored quickly. */}
                {issueSeries.length > 0 && (
                  <div className="mt-2 flex justify-center">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                      {[...issueSeries].reverse().map((s) => {
                        const visible = selectedIssueIds.has(s.issueId);
                        return <button
                          type="button"
                          key={`legend_${s.key}`}
                          className={`flex items-center gap-2 text-left ${visible ? "" : "opacity-40"}`}
                          onClick={() => {
                            const next = new Set(selectedIssueIds);
                            if (visible) next.delete(s.issueId); else next.add(s.issueId);
                            onIssueFilterChange?.(next.size === issueSeries.length
                              ? ALL_DIMENSION_VALUES
                              : includeDimensionValueIds(issueSeries.filter((series) => next.has(series.issueId)).map((series) => series.issueId)));
                          }}
                          aria-pressed={visible}
                          title={`${visible ? "Hide" : "Show"} ${s.name}`}
                        >
                          <span
                            className="inline-block h-2.5 w-4 border border-border"
                            style={{ background: s.color }}
                          />
                          <span className="text-app">{s.name}</span>
                        </button>;
                      })}
                    </div>
                  </div>
                )}

                {byIssueTableRows.length > 0 && (
                  <div className="mt-4">
                    <ExcelGrid columns={byIssueColumns} rows={byIssueTableRows} />
                  </div>
                )}
              </div>

              {/* Chart 2 (SECOND): Aggregate Principal + Interest, with table inside this card */}
              <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
                <h3 className="text-lg font-semibold text-brand mb-2">
                  Principal and Interest
                </h3>

                <div className="bg-card border border-border rounded-xl p-4 shadow-sm h-96">
                  {visibleIssueSeries.length ? <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={aggregateFY.map((r) => ({
                        ...r,
                        period: periodLabel(r.date),
                      }))}
                      margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis
                        domain={[aggScale.niceMin, aggScale.niceMax]}
                        ticks={aggScale.ticks}
                        tickFormatter={yTickFormatter}
                      />
                      <Tooltip content={<ChartTooltip formatter={tooltipFormatter} />} />
                      <Legend
                        verticalAlign="bottom"
                        align="center"
                        height={18}
                        wrapperStyle={{
                          bottom: 0,
                          fontSize: 13,
                          lineHeight: "1em",
                          marginTop: 0,
                          padding: 0,
                        }}
                      />

                      <Bar
                        dataKey="interest"
                        stackId="a"
                        fill={CHART_VARS_PI.interest}
                        name="Interest"
                        isAnimationActive={false}
                      />
                      <Bar
                        dataKey="principal"
                        stackId="a"
                        fill={CHART_VARS_PI.principal}
                        name="Principal"
                        isAnimationActive={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer> : (
                    <div className="flex h-full items-center justify-center text-sm text-muted">
                      No issues selected. Use the Issues filter to choose chart constituents.
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <ExcelGrid columns={columns} rows={tableRows} />
                </div>
              </div>
            </div>
          </div>
        )}
      </SectionCard>
      ) : (
        <SectionCard
          title="Scheduled Debt Service by Date or Period"
          headerAction={
            <PdfButton
              onClick={handlePeriodPdfExport}
              generating={periodPdfGenerating}
              disabled={
                !generatedCriteria || reportRefreshRequired || !periodRows.length
              }
              title="Export the generated scheduled debt service report to PDF"
            />
          }
        >
          <div className="mt-2 flex flex-wrap items-end gap-4">
            <div className="flex flex-col">
              <span className="mb-1 text-xs font-medium text-muted">Report Type</span>
              <div className="flex h-8 overflow-hidden rounded-md border border-input bg-background shadow-sm">
                {[
                  { key: "date", label: "Date" },
                  { key: "period", label: "Period" },
                ].map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`px-3 text-sm transition-colors ${
                      reportType === option.key
                        ? "btn-primary"
                        : "text-foreground hover:bg-muted/50"
                    }`}
                    onClick={() => setReportType(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {reportType === "period" ? (
              <>
                <DateInput
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  maxDate={endDate || ""}
                  className="min-w-48"
                />
                <DateInput
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  minDate={startDate || ""}
                  className="min-w-48"
                />
              </>
            ) : (
              <DateInput
                label="Selected Date"
                value={selectedDate}
                onChange={setSelectedDate}
                className="min-w-48"
              />
            )}

            <button
              type="button"
              className="btn btn-primary text-sm"
              disabled={!periodCriteriaValid || loading}
              onClick={() => setGeneratedCriteria(currentCriteria)}
            >
              {generatedCriteria ? "Refresh" : "Generate"}
            </button>
          </div>

          {error ? (
            <div className="mt-4 text-sm text-red-600">{error}</div>
          ) : null}

          {periodPdfError ? (
            <div className="mt-4 text-sm text-red-600">{periodPdfError}</div>
          ) : null}

          {reportRefreshRequired ? (
            <div className="mt-4 rounded-md border border-border bg-muted/20 p-3 text-sm text-muted">
              Report criteria changed. Select Refresh to update the results.
            </div>
          ) : null}

          {generatedCriteria && !reportRefreshRequired ? (
            periodRows.length ? (
              <div className="mt-5 space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <KpiTile label="Total Principal" value={fmtMoney2(periodTotals.principal)} />
                  <KpiTile label="Total Interest" value={fmtMoney2(periodTotals.interest)} />
                  <KpiTile label="Total Debt Service" value={fmtMoney2(periodTotals.total)} />
                </div>
                <ExcelGrid columns={periodColumns} rows={periodRows} />
                <div className="text-xs text-muted">
                  Scheduled amounts shown from saved issue structures; they do not indicate payment status.
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-md border border-border bg-muted/20 p-4 text-sm text-muted">
                No scheduled debt service exists for the selected {generatedCriteria.reportType}.
              </div>
            )
          ) : null}
        </SectionCard>
      )}
    </div>
  );
}
